import { useGetAllCriticality } from "@features/ax/assetRegistry/systems/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { HelpCircle } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUpdateSystemCriticality } from "../../hooks";

interface CriticalityItem {
  name: string;
  code: string;
  description: string;
  weightage: number;
  isActive: boolean;
  score?: number;
}

interface CriticalityAssessmentData {
  criticalityScores: Record<string, number>;
  finalScore: number;
  level: string;
}

interface CriticalityAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  existingScores?: Record<string, number>;
  existingFinalScore?: number;
  title?: string;
  entityName?: string;
  systemType?: any;
}

const CriticalityAssessmentModal: React.FC<CriticalityAssessmentModalProps> = ({
  open,
  onClose,
  existingScores,
  existingFinalScore,
  title = "Criticality Assessment",
  entityName,
  systemType,
}) => {
  const initializationRef = useRef(false);
  const remoteUtilityId = useRemoteUtilityId();
  // Fetch criticality data from API
  const {
    data: criticality,
    isLoading: isCriticalityLoading,
    isError: isCriticalityError,
  } = useGetAllCriticality();

  const { mutateAsync: updateCriticality, isPending: isUpdating } =
    useUpdateSystemCriticality();

  // Initialize dynamic criticality fields with default value of 3
  const initializeDynamicFields = useMemo(() => {
    if (!criticality || !Array.isArray(criticality)) return {};

    const dynamicFields: Record<string, number> = {};
    criticality.forEach((item) => {
      const fieldKey = item.code.split("#")[0];
      dynamicFields[fieldKey] = 3; // Default to 3
    });

    return dynamicFields;
  }, [criticality]);

  // Local state for form data
  const [localFormData, setLocalFormData] = useState<Record<string, number>>(
    initializeDynamicFields
  );

  // Initialize form data when modal opens
  useEffect(() => {
    if (open && !initializationRef.current) {
      if (existingScores && Object.keys(existingScores).length > 0) {
        // Use existing scores if provided (edit mode)
        setLocalFormData(existingScores);
      } else {
        // Use default values
        setLocalFormData(initializeDynamicFields);
      }
      initializationRef.current = true;
    }
  }, [open, existingScores, initializeDynamicFields]);

  // Reset initialization when modal closes
  useEffect(() => {
    if (!open) {
      initializationRef.current = false;
    }
  }, [open]);

  // Generate criticality slider fields dynamically from API data
  const generateCriticalityFields = useMemo((): FormField[] => {
    if (!criticality || !Array.isArray(criticality)) return [];

    return criticality
      .filter((item) => item.isActive === true)
      .map((item) => {
        const fieldKey = item.code.split("#")[0];

        return {
          name: fieldKey,
          type: "slider" as const,
          label: `${item.name} (${item.weightage}%)`,
          min: 1,
          max: 5,
          step: 1,
          defaultValue: 3,
          showHelperTooltip: true,
          tooltipText: item.description || `Assessment for ${item.name}`,
          className: "col-span-2",
        } as FormField;
      });
  }, [criticality]);

  // Create form instance
  const form = FormService.createForm({
    fields: generateCriticalityFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  useEffect(() => {
    if (systemType && criticality && Array.isArray(criticality)) {
      const criticalityScores: Record<string, number> = {};

      if (
        systemType?.criticalityAssessment &&
        Array.isArray(systemType.criticalityAssessment)
      ) {
        // Case 1: systemType.criticalityAssessment exists - use existing scores
        systemType.criticalityAssessment.forEach((item: any) => {
          if (item.code && item.score !== undefined) {
            const fieldKey = item.code.split("#")[0];
            criticalityScores[fieldKey] = item.score;
          }
        });
      } else {
        // Case 2: systemType.criticalityAssessment doesn't exist - use default score of 3
        criticality
          .filter((item) => item.isActive === true)
          .forEach((item) => {
            const fieldKey = item.code.split("#")[0];
            criticalityScores[fieldKey] = 3; // Default to 3
          });
      }

      // Set form values for all fields
      Object.keys(initializeDynamicFields).forEach((key) => {
        const score =
          criticalityScores[key] !== undefined ? criticalityScores[key] : 3;
        form.setValue(key, score);
      });

      // Update local form data
      setLocalFormData(criticalityScores);
    }
  }, [systemType, criticality, initializeDynamicFields, form]);

  // Get criticality field keys
  const criticalityKeys = useMemo(
    () =>
      (criticality ?? [])
        .filter((i) => i.isActive)
        .map((i) => i.code.split("#")[0]),
    [criticality]
  );

  // Watch slider fields so component re-renders when they change
  const watchedValues = form.watch(criticalityKeys);

  // Calculate criticality score
  const { score: criticalityScore, level: criticalityLevel } = useMemo(() => {
    try {
      if (!criticality) return { score: 0, level: "Low" };

      const activeItems = criticality.filter((item) => item.isActive === true);
      if (activeItems.length === 0) return { score: 0, level: "Low" };

      let totalWeightage = 0;
      let weightedSum = 0;

      activeItems.forEach((item) => {
        const fieldKey = item.code.split("#")[0];
        const scoreValue = form.getValues(fieldKey);

        if (
          scoreValue !== undefined &&
          scoreValue !== null &&
          !isNaN(Number(scoreValue))
        ) {
          const weight = item.weightage / 100;
          weightedSum += Number(scoreValue) * weight;
          totalWeightage += weight;
        }
      });

      const criticalityScore =
        totalWeightage > 0 ? weightedSum / totalWeightage : 0;

      let criticalityLevel: string;
      if (criticalityScore >= 4) criticalityLevel = "High";
      else if (criticalityScore >= 3) criticalityLevel = "Medium";
      else if (criticalityScore >= 2) criticalityLevel = "Medium";
      else if (criticalityScore >= 1) criticalityLevel = "Low";
      else criticalityLevel = "Low";

      return { score: criticalityScore, level: criticalityLevel };
    } catch (error) {
      return { score: 0, level: "Low" };
    }
  }, [criticality, watchedValues, form]);

  // Get criticality badge variant
  const getCriticality = (score: number) => {
    if (score >= 0 && score <= 25) {
      return { level: "Low", variant: "success" as const };
    } else if (score <= 50) {
      return { level: "Medium", variant: "warning" as const };
    } else if (score <= 75) {
      return { level: "High", variant: "destructive" as const };
    } else {
      return { level: "Critical", variant: "secondary" as const };
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...localFormData,
      [fieldName]: value,
    };

    setLocalFormData(updatedData);
  };

  // Handle Save
  const handleSave = async () => {
    const formData = form.getValues(); // Get all current form values

    const criticalityAssessment =
      criticality
        ?.filter((item) => item.isActive === true)
        .map((item) => {
          const fieldKey = item.code.split("#")[0]; // Use consistent field key extraction

          return {
            name: item.name,
            score: formData[fieldKey] ?? 3, // Use form values instead of localFormData
            code: item.code,
            weight: item.weightage,
          };
        }) || [];

    const payload = {
      code: systemType?.code,
      name: systemType?.name,
      remote_utility_id: remoteUtilityId,
      criticality_assessment: criticalityAssessment,
    };

    await updateCriticality(payload);
    onClose();
  };

  // Handle Cancel
  const handleCancel = () => {
    onClose();
  };

  const { level, variant } = getCriticality(criticalityScore * 20);

  // Loading state
  if (isCriticalityLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading criticality parameters...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (isCriticalityError) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-4">
                Failed to load criticality parameters.
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title}
            {entityName && (
              <span className="text-gray-600 font-normal ml-2">
                - {entityName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Criticality Assessment Form */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              Criticality Assessment Parameters
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Adjust the sliders below to assess each criticality parameter.
              Each parameter is weighted and contributes to the overall
              criticality score.
            </p>

            {generateCriticalityFields.length > 0 ? (
              <DynamicForm
                fields={generateCriticalityFields}
                form={form}
                onFieldChange={handleFieldChange}
                gridColumns={2}
                responsiveColumns={{
                  sm: 1,
                  md: 2,
                }}
                config={{
                  layout: "grid",
                  columns: 2,
                  showResetButton: false,
                  showSubmitButton: false,
                  spacing: "medium",
                  classes: {
                    form: "space-y-6 p-0",
                    slider: "w-full",
                    label: "block text-sm font-medium text-gray-700",
                    container: "space-y-1",
                    helperText: "text-sm text-gray-500 mt-1",
                  },
                }}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                No criticality parameters available.
              </div>
            )}
          </div>

          {/* Final Criticality Score Display */}
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Final Criticality Score
                  </p>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          This score is automatically calculated based on the
                          weighted average of all assessed parameters, providing
                          an overall measure of criticality. The score helps
                          prioritize maintenance activities, emergency response
                          planning, and resource allocation decisions.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {Math.round(criticalityScore * 20)}
                  <span className="text-lg text-gray-500 ml-1">/ 100</span>
                </p>
              </div>
              <Badge variant={variant} className="text-lg px-4 py-2">
                {level}
              </Badge>
            </div>
          </div>

          {/* Assessment Guidelines */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              Assessment Guidelines
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                • <strong>Score 1:</strong> Minimal impact on operations
              </li>
              <li>
                • <strong>Score 2:</strong> Low impact, can tolerate downtime
              </li>
              <li>
                • <strong>Score 3:</strong> Moderate impact, requires planning
              </li>
              <li>
                • <strong>Score 4:</strong> High impact, critical for operations
              </li>
              <li>
                • <strong>Score 5:</strong> Mission critical, immediate action
                required
              </li>
            </ul>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t flex justify-end">
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isUpdating}>
                Save Assessment
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CriticalityAssessmentModal;
